import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { ActionIconButton } from './ui/ActionIconButton';
import { Button } from './ui/button';
import SegmentedSwitch from './scheduledJobs/SegmentedSwitch';
import {
  XMarkIcon,
  ClipboardDocumentListIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

// Same tokened pill/textarea chrome as the Create/Edit modal in
// pages/SiteLists.tsx — this "paste" step is a second copy of that form
// (deepcontext-gncj).
const PILL_INPUT = 'w-full rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';
const DOMAIN_TEXTAREA = 'w-full rounded-xl border border-line bg-transparent px-3.5 py-3 text-sm font-mono text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';

interface SiteList {
  id: string;
  name: string;
  description?: string;
  listType: 'include' | 'exclude';
  domains: string[];
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SelectedDomainInfo {
  domain: string;
  listId: string;
  listName: string;
  listType: 'include' | 'exclude';
}

interface SiteListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedDomains: SelectedDomainInfo[]) => void;
  initialSelectedDomains?: SelectedDomainInfo[];
}

const MAX_DOMAINS = 200;

const SiteListModal: React.FC<SiteListModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialSelectedDomains,
}) => {
  const [step, setStep] = useState<'select' | 'paste'>('select');
  const [siteLists, setSiteLists] = useState<SiteList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded lists state
  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set());

  // Selected domains state - Map of "listId:domain" -> SelectedDomainInfo
  const [selectedDomainsMap, setSelectedDomainsMap] = useState<Map<string, SelectedDomainInfo>>(new Map());

  // Paste list form state
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [listType, setListType] = useState<'include' | 'exclude'>('include');
  const [domainsText, setDomainsText] = useState('');
  const [parsedDomains, setParsedDomains] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadSiteLists();
      setStep('select');
      setSearchQuery('');
      setExpandedLists(new Set());
      // Initialize selection state from initialSelectedDomains
      const initialMap = new Map<string, SelectedDomainInfo>();
      if (initialSelectedDomains) {
        initialSelectedDomains.forEach((info) => {
          const key = `${info.listId}:${info.domain}`;
          initialMap.set(key, info);
        });
      }
      setSelectedDomainsMap(initialMap);
      resetForm();
    }
  }, [isOpen, initialSelectedDomains]);

  // Parse domains when text changes
  useEffect(() => {
    const domains = parseDomains(domainsText);
    setParsedDomains(domains);
  }, [domainsText]);

  // Filter site lists and domains based on search
  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) {
      return siteLists.map((list) => ({
        ...list,
        matchingDomains: list.domains,
        isNameMatch: true,
      }));
    }

    return siteLists
      .map((list) => {
        const isNameMatch = list.name.toLowerCase().includes(query);
        const isDescriptionMatch = list.description?.toLowerCase().includes(query);
        const matchingDomains = list.domains.filter((d) => d.toLowerCase().includes(query));

        return {
          ...list,
          matchingDomains: isNameMatch || isDescriptionMatch ? list.domains : matchingDomains,
          isNameMatch: isNameMatch || isDescriptionMatch || matchingDomains.length > 0,
        };
      })
      .filter((list) => list.isNameMatch);
  }, [siteLists, searchQuery]);

  // Auto-expand lists with matching domains when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const listsWithMatchingDomains = filteredData
        .filter((list) => {
          const query = searchQuery.toLowerCase().trim();
          return list.domains.some((d) => d.toLowerCase().includes(query));
        })
        .map((list) => list.id);
      setExpandedLists(new Set(listsWithMatchingDomains));
    }
  }, [searchQuery, filteredData]);

  // Separate by type for better organization
  const includeLists = filteredData.filter((l) => l.listType === 'include');
  const excludeLists = filteredData.filter((l) => l.listType === 'exclude');

  const loadSiteLists = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/site-lists');
      setSiteLists(response.data);
    } catch (err: any) {
      console.error('Failed to load site lists:', err);
      setError('Failed to load site lists');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setListName('');
    setListDescription('');
    setListType('include');
    setDomainsText('');
    setParsedDomains([]);
    setError(null);
  };

  const parseDomains = (text: string): string[] => {
    if (!text.trim()) return [];

    const lines = text.split(/[\n,;]+/);
    const domains: string[] = [];
    const seen = new Set<string>();

    for (const line of lines) {
      let domain = line.trim();
      if (!domain) continue;

      domain = domain.replace(/^https?:\/\//, '');
      domain = domain.replace(/^www\./, '');
      domain = domain.split('/')[0].split('?')[0].split('#')[0];
      domain = domain.split(':')[0];
      domain = domain.toLowerCase();

      if (domain && domain.includes('.') && /^[a-z0-9.-]+$/.test(domain)) {
        if (!seen.has(domain)) {
          seen.add(domain);
          domains.push(domain);
        }
      }
    }

    return domains;
  };

  const handleCreateList = async () => {
    if (!listName.trim()) {
      setError('List name is required');
      return;
    }

    if (parsedDomains.length === 0) {
      setError('At least one valid domain is required');
      return;
    }

    if (parsedDomains.length > MAX_DOMAINS) {
      setError(`Maximum ${MAX_DOMAINS} domains allowed`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/api/site-lists', {
        name: listName.trim(),
        description: listDescription.trim() || undefined,
        listType,
        domains: parsedDomains,
      });

      const newList: SiteList = response.data;
      // Convert new list to selectedDomainInfos format and select all domains
      const domainInfos: SelectedDomainInfo[] = newList.domains.map((domain) => ({
        domain,
        listId: newList.id,
        listName: newList.name,
        listType: newList.listType,
      }));
      onSelect(domainInfos);
      onClose();
    } catch (err: any) {
      console.error('Failed to create site list:', err);
      setError(err.response?.data?.error || 'Failed to create site list');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDomain = (list: SiteList, domain: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const key = `${list.id}:${domain}`;

    setSelectedDomainsMap((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(key)) {
        newMap.delete(key);
      } else {
        newMap.set(key, {
          domain,
          listId: list.id,
          listName: list.name,
          listType: list.listType,
        });
      }
      return newMap;
    });
  };

  const handleSelectAllDomainsInList = (list: SiteList, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDomainsMap((prev) => {
      const newMap = new Map(prev);
      // Check if all domains in this list are already selected
      const allSelected = list.domains.every((d) => newMap.has(`${list.id}:${d}`));

      if (allSelected) {
        // Deselect all domains from this list
        list.domains.forEach((d) => {
          newMap.delete(`${list.id}:${d}`);
        });
      } else {
        // Select all domains from this list
        list.domains.forEach((d) => {
          const key = `${list.id}:${d}`;
          newMap.set(key, {
            domain: d,
            listId: list.id,
            listName: list.name,
            listType: list.listType,
          });
        });
      }
      return newMap;
    });
  };

  const handleConfirmSelection = () => {
    onSelect(Array.from(selectedDomainsMap.values()));
    onClose();
  };

  const handleClearSelection = () => {
    setSelectedDomainsMap(new Map());
  };

  const handleDeleteList = async (listId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this site list?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/site-lists/${listId}`);
      await loadSiteLists();
      // Remove any selected domains from the deleted list
      setSelectedDomainsMap((prev) => {
        const newMap = new Map<string, SelectedDomainInfo>();
        prev.forEach((info, key) => {
          if (info.listId !== listId) {
            newMap.set(key, info);
          }
        });
        return newMap;
      });
    } catch (err: any) {
      console.error('Failed to delete site list:', err);
      setError(err.response?.data?.error || 'Failed to delete site list');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (listId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedLists((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-warn-bg px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const renderSiteListItem = (list: (typeof filteredData)[0]) => {
    const isExpanded = expandedLists.has(list.id);
    const query = searchQuery.toLowerCase().trim();
    const hasMatchingDomains =
      query && list.domains.some((d) => d.toLowerCase().includes(query));

    // Count selected domains from this list
    const selectedCount = list.domains.filter((d) => selectedDomainsMap.has(`${list.id}:${d}`)).length;
    const allSelected = selectedCount === list.domains.length;
    const someSelected = selectedCount > 0 && selectedCount < list.domains.length;
    const hasSelection = selectedCount > 0;

    return (
      <div key={list.id} className="border rounded-lg overflow-hidden">
        {/* List Header */}
        <div
          className={`p-3 transition-all ${
            hasSelection ? 'bg-nav-active' : 'bg-surface'
          }`}
        >
          <div className="flex items-center gap-3">
            {/* Checkbox for all domains */}
            <div
              onClick={(e) => handleSelectAllDomainsInList(list, e)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer ${
                allSelected
                  ? 'bg-accent border-accent'
                  : someSelected
                  ? 'bg-accent border-accent'
                  : 'border-line bg-surface hover:border-accent'
              }`}
            >
              {allSelected && <CheckIcon className="h-3.5 w-3.5 text-white" />}
              {someSelected && <div className="w-2 h-0.5 bg-surface rounded" />}
            </div>

            {/* Expand/Collapse Button */}
            <button
              onClick={(e) => toggleExpanded(list.id, e)}
              className="p-1 hover:bg-ghost-bg rounded flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-ink-muted" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-ink-muted" />
              )}
            </button>

            {/* List Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-ink">
                  {highlightMatch(list.name, searchQuery)}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    list.listType === 'include'
                      ? 'bg-safe-bg text-safe'
                      : 'bg-risk-bg text-risk'
                  }`}
                >
                  {list.listType}
                </span>
              </div>
              <p className="text-sm text-ink-muted">
                {hasSelection ? (
                  <span className="text-accent font-medium">
                    {selectedCount} of {list.domains.length} selected
                  </span>
                ) : (
                  <>
                    {list.domains.length} domain{list.domains.length !== 1 ? 's' : ''}
                    {hasMatchingDomains && (
                      <span className="text-accent ml-1">
                        ({list.matchingDomains.length} matching)
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>

            {/* Delete Button */}
            <ActionIconButton
              kind="delete"
              onClick={(e) => handleDeleteList(list.id, e)}
              title="Delete site list"
              className="flex-shrink-0"
            >
              <TrashIcon className="h-4 w-4" />
            </ActionIconButton>
          </div>
        </div>

        {/* Expanded Domains List */}
        {isExpanded && (
          <div className="border-t bg-ghost-bg p-3 max-h-48 overflow-y-auto">
            <div className="space-y-1">
              {(query ? list.matchingDomains : list.domains).map((domain, idx) => {
                const key = `${list.id}:${domain}`;
                const isDomainSelected = selectedDomainsMap.has(key);
                return (
                  <div
                    key={idx}
                    onClick={(e) => handleToggleDomain(list, domain, e)}
                    className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                      isDomainSelected ? 'bg-nav-active' : 'hover:bg-ghost-bg'
                    }`}
                  >
                    {/* Domain Checkbox */}
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isDomainSelected
                          ? 'bg-accent border-accent'
                          : 'border-line bg-surface'
                      }`}
                    >
                      {isDomainSelected && <CheckIcon className="h-3 w-3 text-white" />}
                    </div>
                    <span
                      className={`text-sm font-mono ${isDomainSelected ? 'text-accent-active' : 'text-ink'}`}
                    >
                      {highlightMatch(domain, searchQuery)}
                    </span>
                  </div>
                );
              })}
              {query && list.matchingDomains.length < list.domains.length && (
                <p className="text-xs text-ink-muted pt-2">
                  + {list.domains.length - list.matchingDomains.length} more domains not matching
                  search
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step === 'paste' && (
              <ActionIconButton kind="ghost" onClick={() => setStep('select')} aria-label="Back">
                <ArrowLeftIcon className="h-5 w-5" />
              </ActionIconButton>
            )}
            <div>
              <h3 className="text-lg font-semibold">
                {step === 'select' ? 'Domains & Apps Import' : 'Create Site List'}
              </h3>
              {step === 'select' && (
                <p className="text-sm text-ink-muted">Pick a reusable SiteList</p>
              )}
            </div>
          </div>
          <ActionIconButton kind="ghost" onClick={onClose} aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </ActionIconButton>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-risk-bg border border-risk rounded-lg flex items-center gap-2 text-risk">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'select' && (
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by site list name or domain..."
                  className="w-full pl-10 pr-4 py-2.5 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                  autoFocus
                />
              </div>

              {/* Create Button */}
              <button
                onClick={() => setStep('paste')}
                className="w-full p-3 border-2 border-dashed border-line rounded-lg hover:border-accent hover:bg-nav-active transition-colors flex items-center justify-center gap-2"
              >
                <span className="font-medium text-ink">Create Site List</span>
              </button>

              {/* Loading State */}
              {loading && (
                <div className="py-8 text-center">
                  <div className="animate-spin h-6 w-6 border-2 border-accent border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-ink-muted">Loading site lists...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && siteLists.length === 0 && (
                <div className="py-8 text-center">
                  <ClipboardDocumentListIcon className="h-12 w-12 text-ink-muted mx-auto mb-2" />
                  <p className="text-ink-muted">No site lists created yet</p>
                  <p className="text-sm text-ink-muted">Create one to filter URLs by domain</p>
                </div>
              )}

              {/* No Search Results */}
              {!loading && siteLists.length > 0 && filteredData.length === 0 && (
                <div className="py-6 text-center">
                  <p className="text-ink-muted">No lists or domains match "{searchQuery}"</p>
                </div>
              )}

              {/* Site Lists - Include */}
              {includeLists.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-ink-muted uppercase tracking-wider mb-2">
                    Include Lists ({includeLists.length})
                  </p>
                  <div className="space-y-2">{includeLists.map(renderSiteListItem)}</div>
                </div>
              )}

              {/* Site Lists - Exclude */}
              {excludeLists.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-ink-muted uppercase tracking-wider mb-2">
                    Exclude Lists ({excludeLists.length})
                  </p>
                  <div className="space-y-2">{excludeLists.map(renderSiteListItem)}</div>
                </div>
              )}

              {/* Remove selection button - only shown when there are initial selections */}
              {initialSelectedDomains && initialSelectedDomains.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedDomainsMap(new Map());
                    onSelect([]);
                    onClose();
                  }}
                  className="w-full p-2.5 text-sm text-risk hover:bg-risk-bg rounded-lg border border-risk font-medium"
                >
                  Remove All Domain Filters
                </button>
              )}
            </div>
          )}

          {step === 'paste' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">List Name *</label>
                <input
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="e.g., Vietnam News Sites"
                  className={PILL_INPUT}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={listDescription}
                  onChange={(e) => setListDescription(e.target.value)}
                  placeholder="Brief description of this list"
                  className={PILL_INPUT}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-2">List Type</label>
                <SegmentedSwitch
                  label="List Type"
                  value={listType}
                  onChange={setListType}
                  options={[
                    { value: 'include', label: 'Include' },
                    { value: 'exclude', label: 'Exclude' },
                  ]}
                />
                <p className="mt-1.5 text-xs text-ink-muted">
                  {listType === 'include' ? 'Only scan these domains' : 'Skip these domains'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">Domains *</label>
                <textarea
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  placeholder={`Paste domains here, one per line or comma-separated:\nexample.com\nhttps://www.news-site.com/article\nother-domain.org`}
                  className={DOMAIN_TEXTAREA}
                  rows={6}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-ink-muted">URLs will be automatically cleaned</p>
                  <p
                    className={`text-xs font-medium ${parsedDomains.length > MAX_DOMAINS ? 'text-risk' : 'text-ink-muted'}`}
                  >
                    {parsedDomains.length} / {MAX_DOMAINS}
                  </p>
                </div>
              </div>

              {/* Preview parsed domains — canvas-inset box, matching the
                  locked modal spec. */}
              {parsedDomains.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-ink mb-1">
                    Preview ({parsedDomains.length} domains)
                  </p>
                  <div className="max-h-24 overflow-y-auto p-3 bg-canvas rounded-xl">
                    <div className="flex flex-wrap gap-1.5">
                      {parsedDomains.slice(0, 30).map((domain, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 bg-surface border border-line rounded-full text-xs font-mono text-ink"
                        >
                          {domain}
                        </span>
                      ))}
                      {parsedDomains.length > 30 && (
                        <span className="px-2 py-0.5 text-xs text-ink-muted">
                          +{parsedDomains.length - 30} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between items-center">
          <div className="text-sm text-ink-muted">
            {step === 'select' && selectedDomainsMap.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-accent font-medium">
                  {selectedDomainsMap.size} domain{selectedDomainsMap.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={handleClearSelection}
                  className="text-xs text-risk hover:opacity-80 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {step === 'select' && (
              <Button type="button" onClick={handleConfirmSelection} disabled={loading}>
                {selectedDomainsMap.size > 0 ? 'Confirm Selection' : 'Clear Selection'}
              </Button>
            )}
            {step === 'paste' && (
              <Button
                type="button"
                onClick={handleCreateList}
                disabled={
                  loading ||
                  parsedDomains.length === 0 ||
                  parsedDomains.length > MAX_DOMAINS ||
                  !listName.trim()
                }
              >
                {loading ? 'Creating...' : 'Create & Select'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteListModal;
